import FieldError from "../components/FieldError";

const textFields = [
  ["name", "Name"],
  ["slug", "Slug"],
  ["address_line1", "Address line 1"],
  ["address_line2", "Address line 2"],
  ["city", "City"],
  ["state", "State"],
  ["postal_code", "Postal code"],
  ["country", "Country"],
  ["phone", "Phone"],
  ["email", "Email"],
];

const paymentDefaults = {
  stripe_enabled: true,
  cash_enabled: false,
  cash_instructions: "",
  zelle_enabled: false,
  zelle_recipient_name: "",
  zelle_recipient_email: "",
  zelle_recipient_phone: "",
  zelle_instructions: "",
  cashapp_enabled: false,
  cashapp_cashtag: "",
  cashapp_recipient_name: "",
  cashapp_instructions: "",
  payment_notes: "",
  minimum_order: "0.00",
};

function PaymentToggle({ checked, label, onChange }) {
  return (
    <label>
      <input checked={Boolean(checked)} type="checkbox" onChange={(event) => onChange(event.target.checked)} />
      {label}
    </label>
  );
}

function MoneyInput({ disabled = false, error, helperText, label, name, onChange, value }) {
  const errorId = `location-${name}-error`;

  return (
    <label className="form-group">
      <span className="form-label">{label}</span>
      <span className="input-affix input-affix--currency">
        <span>$</span>
        <input
          aria-describedby={error ? errorId : undefined}
          aria-invalid={Boolean(error)}
          disabled={disabled}
          inputMode="decimal"
          value={value || ""}
          onChange={(event) => onChange({ [name]: event.target.value })}
        />
      </span>
      {helperText ? <small className="muted-text">{helperText}</small> : null}
      <FieldError id={errorId} message={error} />
    </label>
  );
}

function PercentInput({ error, helperText, label, name, onChange, value }) {
  const errorId = `location-${name}-error`;

  return (
    <label className="form-group">
      <span className="form-label">{label}</span>
      <span className="input-affix input-affix--percent">
        <input
          aria-describedby={error ? errorId : undefined}
          aria-invalid={Boolean(error)}
          inputMode="decimal"
          value={value || ""}
          onChange={(event) => onChange({ [name]: event.target.value })}
        />
        <span>%</span>
      </span>
      {helperText ? <small className="muted-text">{helperText}</small> : null}
      <FieldError id={errorId} message={error} />
    </label>
  );
}

function LocationForm({ form, fieldErrors, saving, onCancel, onChange, onSubmit }) {
  const paymentSettings = {
    ...paymentDefaults,
    ...(form.payment_settings || {}),
  };

  function updatePaymentSettings(changes) {
    onChange({
      payment_settings: {
        ...paymentSettings,
        ...changes,
      },
    });
  }

  return (
    <form className="card admin-panel" onSubmit={onSubmit}>
      <div className="card-body flex flex-column gap-4">
        <div className="form-row">
          {textFields.map(([name, label]) => {
            const errorId = `location-${name}-error`;

            return (
              <label className="form-group" key={name}>
                <span className="form-label">{label}</span>
                <input
                  aria-describedby={fieldErrors[name] ? errorId : undefined}
                  aria-invalid={Boolean(fieldErrors[name])}
                  name={name}
                  value={form[name] || ""}
                  onChange={(event) => onChange({ [name]: event.target.value })}
                />
                <FieldError id={errorId} message={fieldErrors[name]} />
              </label>
            );
          })}
        </div>

        <section className="admin-form-section">
          <div>
            <p className="eyebrow">Ordering and fees</p>
            <h2>Location order settings</h2>
            <p className="muted-text">Stripe remains configured separately under Payment Options.</p>
          </div>

          <div className="checkbox-group">
            <label>
              <input
                checked={Boolean(form.accepts_pickup)}
                type="checkbox"
                onChange={(event) => onChange({ accepts_pickup: event.target.checked })}
              />
              Pickup enabled
            </label>
            <label>
              <input
                checked={Boolean(form.accepts_delivery)}
                type="checkbox"
                onChange={(event) => onChange({ accepts_delivery: event.target.checked })}
              />
              Delivery enabled
            </label>
            <label>
              <input
                checked={Boolean(form.accepts_catering)}
                type="checkbox"
                onChange={(event) => onChange({ accepts_catering: event.target.checked })}
              />
              Catering enabled
            </label>
            <label>
              <input
                checked={Boolean(form.is_active)}
                type="checkbox"
                onChange={(event) => onChange({ is_active: event.target.checked })}
              />
              Active
            </label>
          </div>

          <div className="form-row">
            <PercentInput
              error={fieldErrors.tax_rate_percent}
              helperText="Percentage applied to taxable items for orders from this location."
              label="Tax rate"
              name="tax_rate_percent"
              value={form.tax_rate_percent}
              onChange={onChange}
            />
            <MoneyInput
              disabled={!form.accepts_delivery}
              error={fieldErrors.delivery_fee}
              helperText="Flat fee added only when the customer chooses delivery."
              label="Delivery fee"
              name="delivery_fee"
              value={form.delivery_fee}
              onChange={onChange}
            />
            <label className="form-group">
              <span className="form-label">Service fee enabled</span>
              <label>
                <input
                  checked={Boolean(form.service_fee_enabled)}
                  type="checkbox"
                  onChange={(event) => onChange({ service_fee_enabled: event.target.checked })}
                />
                Add service fee to orders
              </label>
            </label>
            <MoneyInput
              disabled={!form.service_fee_enabled}
              error={fieldErrors.service_fee}
              helperText="Optional flat fee added to both pickup and delivery orders."
              label="Service fee"
              name="service_fee"
              value={form.service_fee}
              onChange={onChange}
            />
            <label className="form-group form-group-full">
              <span className="form-label">Service fee description</span>
              <textarea
                value={form.service_fee_description || ""}
                onChange={(event) => onChange({ service_fee_description: event.target.value })}
              />
              <small className="muted-text">
                Leave blank to use the default explanation shown to customers.
              </small>
              <FieldError
                id="location-service-fee-description-error"
                message={fieldErrors.service_fee_description}
              />
            </label>
          </div>
        </section>

        <section className="admin-form-section">
          <div>
            <p className="eyebrow">Payment Options</p>
            <h2>Location payment settings</h2>
            <p className="muted-text">Stripe is always available. Manual methods are optional by location.</p>
          </div>

          <div className="checkbox-group">
            <label>
              <input checked readOnly type="checkbox" />
              Stripe enabled
            </label>
            <PaymentToggle
              checked={paymentSettings.cash_enabled}
              label="Accept cash"
              onChange={(cashEnabled) => updatePaymentSettings({ cash_enabled: cashEnabled })}
            />
            <PaymentToggle
              checked={paymentSettings.zelle_enabled}
              label="Accept Zelle"
              onChange={(zelleEnabled) => updatePaymentSettings({ zelle_enabled: zelleEnabled })}
            />
            <PaymentToggle
              checked={paymentSettings.cashapp_enabled}
              label="Accept Cash App"
              onChange={(cashappEnabled) => updatePaymentSettings({ cashapp_enabled: cashappEnabled })}
            />
          </div>

          <div className="form-row">
            <label className="form-group">
              <span className="form-label">Minimum order</span>
              <span className="input-affix input-affix--currency">
                <span>$</span>
                <input
                  inputMode="decimal"
                  value={paymentSettings.minimum_order || ""}
                  onChange={(event) => updatePaymentSettings({ minimum_order: event.target.value })}
                />
              </span>
            </label>
          </div>

          <label className="form-group">
            <span className="form-label">Cash instructions</span>
            <textarea
              value={paymentSettings.cash_instructions || ""}
              onChange={(event) => updatePaymentSettings({ cash_instructions: event.target.value })}
            />
          </label>

          <div className="form-row">
            <label className="form-group">
              <span className="form-label">Zelle recipient name</span>
              <input
                value={paymentSettings.zelle_recipient_name || ""}
                onChange={(event) => updatePaymentSettings({ zelle_recipient_name: event.target.value })}
              />
            </label>
            <label className="form-group">
              <span className="form-label">Zelle email</span>
              <input
                type="email"
                value={paymentSettings.zelle_recipient_email || ""}
                onChange={(event) => updatePaymentSettings({ zelle_recipient_email: event.target.value })}
              />
            </label>
            <label className="form-group">
              <span className="form-label">Zelle phone</span>
              <input
                value={paymentSettings.zelle_recipient_phone || ""}
                onChange={(event) => updatePaymentSettings({ zelle_recipient_phone: event.target.value })}
              />
            </label>
          </div>

          <label className="form-group">
            <span className="form-label">Zelle instructions</span>
            <textarea
              value={paymentSettings.zelle_instructions || ""}
              onChange={(event) => updatePaymentSettings({ zelle_instructions: event.target.value })}
            />
          </label>

          <div className="form-row">
            <label className="form-group">
              <span className="form-label">Cash App cashtag</span>
              <input
                value={paymentSettings.cashapp_cashtag || ""}
                onChange={(event) => updatePaymentSettings({ cashapp_cashtag: event.target.value })}
              />
            </label>
            <label className="form-group">
              <span className="form-label">Cash App recipient name</span>
              <input
                value={paymentSettings.cashapp_recipient_name || ""}
                onChange={(event) => updatePaymentSettings({ cashapp_recipient_name: event.target.value })}
              />
            </label>
          </div>

          <label className="form-group">
            <span className="form-label">Cash App instructions</span>
            <textarea
              value={paymentSettings.cashapp_instructions || ""}
              onChange={(event) => updatePaymentSettings({ cashapp_instructions: event.target.value })}
            />
          </label>

          <label className="form-group">
            <span className="form-label">Payment notes</span>
            <textarea
              value={paymentSettings.payment_notes || ""}
              onChange={(event) => updatePaymentSettings({ payment_notes: event.target.value })}
            />
          </label>
        </section>

        <div className="flex gap-3 flex-wrap">
          <button className="btn btn-primary" disabled={saving} type="submit">
            {saving ? "Saving" : "Save Location"}
          </button>
          <button className="btn btn-secondary" disabled={saving} onClick={onCancel} type="button">
            Cancel
          </button>
        </div>
      </div>
    </form>
  );
}

export default LocationForm;
